import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  clearAuthMethod,
  extractDomain,
  getAuthMethod,
  identifyMixpanel,
  isNewUser,
  setOnceMixpanel,
} from '@monorepo/client-common';
import { UserPayload } from '@monorepo/client-types';
import { useBooleanFeatureFlag } from '@monorepo/feature-flag/client';
import { FLAG } from '@monorepo/feature-flag/shared';
import { useUpdateUserLanguage } from '@monorepo/hub-common';
import {
  AdminTokenKey,
  AnalyticAuthType,
  AnalyticsEventTypes,
  BlockedBy,
  ClientApplicationConfig,
  FailureEventType,
  IsLoggedInKey,
  IsLoggedOutKey,
  User,
} from '@monorepo/types';
import { Application } from '@monorepo/types';
import { CoralogixRum } from '@coralogix/browser';
import { getApplicationConfigQueryOptions } from '@hub/api/queries';
import { useProducts } from '@hub/api/queries/use-products-queries';
import { getEnv } from '@hub/helpers/getters.store';
import { useStore } from '@hub/helpers/use-store';
import gtmService from '@hub/services/analytics/analytics';
import { EnvService } from '@hub/services/core/env-service';
import { initFacebookPixel } from '@hub/utils/meta-pixel-utils';
import { QueryConfigKeys } from '@hub/utils/react-query/query-config';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { setUser } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

const { isProduction } = EnvService;

export const useAppInitialization = () => {
  const {
    dataStore: {
      realTimeStore,
      userStore,
      couponStore,
      contractStore,
      applicationConfigStore,
      programStore,
      groupStore,
    },
    themeStore,
  } = useStore();

  const { apiFactory } = getEnv();
  useBooleanFeatureFlag(FLAG.ENABLE_FUTURES_CART, {
    env: Application.THE5ERS,
  });
  const queryClient = useQueryClient();
  const { refetch } = useProducts();
  const [isAppLoading, setIsAppLoading] = useState<boolean>(true);
  const updateLanguageMutation = useUpdateUserLanguage();

  const getAdminToken = useCallback(() => {
    if (typeof window === 'undefined') return undefined;
    // Check sessionStorage first (we store there after extracting from URL)
    const fromStorage = sessionStorage.getItem(AdminTokenKey);
    if (fromStorage) return fromStorage;
    const url = new URL(window.location.href);
    return url.searchParams.get('admin_token') || undefined;
  }, []);

  const { i18n } = useTranslation();

  const clearCreditQueries = useCallback(() => {
    queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key.length < 2) return false;
        const userCreditHistoryKey =
          QUERY_KEYS[QueryConfigKeys.USER_CREDIT_HISTORY].LIST;
        return (
          (key[0] === userCreditHistoryKey[0] &&
            key[1] === userCreditHistoryKey[1]) ||
          (key[0] === QueryConfigKeys.USERS &&
            (key[1] === 'credits' || key[1] === 'credits-history'))
        );
      },
    });
    // Invalidate all credit-related queries (invalidateQueries with partial key matches all variants)
    queryClient.invalidateQueries({
      queryKey: [QueryConfigKeys.USERS, 'credits'],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryConfigKeys.USERS, 'credits-history'],
    });
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS[QueryConfigKeys.USER_CREDIT_HISTORY].LIST,
    });
  }, [queryClient]);
  // Helper to safely update language only if mutation is not pending
  const updateLanguageIfNeeded = useCallback(
    (languageCode: string) => {
      if (!updateLanguageMutation.isPending) {
        updateLanguageMutation.mutate(languageCode);
      }
    },
    [updateLanguageMutation],
  );

  const init = useCallback(async () => {
    // Initialize theme from localStorage
    themeStore.initTheme();

    // Extract admin_token from URL, store in sessionStorage, and remove from address bar
    // to prevent leaking in telemetry (e.g. first_seen_url)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const token = url.searchParams.get('admin_token');
      if (token) {
        sessionStorage.setItem(AdminTokenKey, token);
        url.searchParams.delete('admin_token');
        window.history.replaceState(null, '', url.toString());
      }
    }

    const adminToken = getAdminToken();

    if (adminToken) {
      userStore.toggleImpersonation();
      // Clear user data when impersonation starts to prevent using stale admin data
      const clearedUser = { ...({} as User) };
      clearedUser.creditBalance = 0;
      userStore.setCurrentUser(clearedUser);
      // Clear cache when impersonation starts
      clearCreditQueries();
    }

    apiFactory.setHttpServiceActions(
      userStore.logout,
      userStore.refreshToken,
      adminToken,
    );

    try {
      if (localStorage.getItem(IsLoggedOutKey)) {
        throw new Error();
      }

      await userStore.getUser();
    } catch {
      clearAuthMethod();
      setIsAppLoading(false);
      userStore.setCurrentUser({} as User);
      userStore.setLoginState(false);
      localStorage.setItem(IsLoggedOutKey, 'true');
      return;
    }

    const localStorageLanguage = localStorage.getItem('i18nextLng');
    const userDbLanguage = userStore.currentUser.applicationLanguage;

    // Wrap language sync in try-catch to ensure initialization continues
    // and clearAuthMethod is eventually called in the finally block below
    try {
      if (localStorageLanguage && userDbLanguage) {
        if (localStorageLanguage !== userDbLanguage) {
          // Use mutation to sync language - only if not already pending
          updateLanguageIfNeeded(localStorageLanguage);
        }
        if (i18n.language !== localStorageLanguage) {
          await i18n.changeLanguage(localStorageLanguage);
        }
      } else if (localStorageLanguage) {
        if (i18n.language !== localStorageLanguage) {
          await i18n.changeLanguage(localStorageLanguage);
        }
      } else if (userDbLanguage) {
        if (i18n.language !== userDbLanguage) {
          await i18n.changeLanguage(userDbLanguage);
        }
        localStorage.setItem('i18nextLng', userDbLanguage);
      } else {
        const fallbackLanguage = i18n.language || i18n.resolvedLanguage || 'en';
        // Use mutation to save fallback - only if not already pending
        updateLanguageIfNeeded(fallbackLanguage);
      }
    } catch (error) {
      console.error('Failed to sync language:', error);
      // Continue with initialization even if language sync fails
    }

    try {
      recordLead({
        email: userStore.currentUser.email,
        firstName: userStore.currentUser.firstName,
        lastName: userStore.currentUser.lastName,
      });

      // Initialize Facebook Pixel
      initFacebookPixel();

      // Initialize a real-time socket
      realTimeStore.initSocket();

      // Prefetch application config using React Query
      const appConfigOptions = getApplicationConfigQueryOptions();
      const appConfig = await queryClient.fetchQuery(appConfigOptions);

      // Update the store directly with type assertion
      applicationConfigStore.updateFromApiResponse(
        appConfig as ClientApplicationConfig,
      );

      await Promise.all([
        refetch(),
        couponStore.getUserCoupons(),
        contractStore.getUserContracts(),
      ]);

      if (isProduction && userStore.currentUser?.email) {
        setUser({ email: userStore.currentUser.email });
      }

      // Identify user in Coralogix RUM
      if (userStore.currentUser?.userId) {
        CoralogixRum.setUserContext({
          user_id: userStore.currentUser.userId,
          user_name: `${userStore.currentUser.firstName} ${userStore.currentUser.lastName}`,
          user_email: userStore.currentUser.email,
        });
      }
      // Identify user in Mixpanel for funnel tracking
      if (userStore.currentUser?.userId) {
        const authMethod = getAuthMethod();
        const userIsNew = isNewUser(userStore.currentUser);

        // Set profile properties - include signup data for new users
        identifyMixpanel({
          uniqueId: userStore.currentUser.userId,
          properties: {
            $email: userStore.currentUser.email,
            $name: userStore.fullName,
            // Add signup-specific properties for new users only
            ...(userIsNew && {
              signup_method: authMethod || 'email',
              signup_date: userStore.currentUser.createdAt,
            }),
          },
        });

        // Set attribution properties ONCE for new users only
        // This prevents overwriting attribution data on subsequent logins
        if (userIsNew) {
          setOnceMixpanel({
            uniqueId: userStore.currentUser.userId,
            properties: {
              $initial_referrer: document.referrer || 'direct',
              $initial_referring_domain: extractDomain(document.referrer),
              first_seen_url: window.location.href,
            },
          });
        }

        // Clean up auth method after all analytics tracking is complete
        clearAuthMethod();
      }

      // Set login state and localStorage at the very end - this triggers navigation to welcome route
      // Only after all data is loaded to avoid partial content rendering
      userStore.setLoginState(true);
      localStorage.removeItem(IsLoggedOutKey);
      localStorage.setItem(IsLoggedInKey, 'true');
      trackLoginEvent(userStore.currentUser.userId);
    } catch (error: unknown) {
      const errorData = error as {
        data?: { statusCode?: number; error?: string };
      };
      if (
        errorData?.data?.statusCode === 403 &&
        errorData?.data?.error === FailureEventType.Block
      ) {
        const updatedAppConfig = {
          ...applicationConfigStore.applicationConfig,
          isBlacklisted: true,
          blockedBy: BlockedBy.FailedPaymentRule,
        };
        applicationConfigStore.updateFromApiResponse(
          updatedAppConfig as ClientApplicationConfig,
        );
      }
      console.error('Error during app initialization:', error);
    } finally {
      clearAuthMethod();
      setIsAppLoading(false);
    }
  }, [
    programStore,
    groupStore,
    getAdminToken,
    realTimeStore,
    userStore,
    apiFactory,
    contractStore,
    couponStore,
    applicationConfigStore,
    themeStore,
    clearCreditQueries,
    updateLanguageIfNeeded,
    i18n,
    refetch,
  ]);

  useEffect(() => {
    init();
  }, [init]);

  // Watch for adminToken changes to clear cache and header when impersonation ends
  useEffect(() => {
    let previousAdminToken = getAdminToken();

    const interval = setInterval(() => {
      const currentAdminToken = getAdminToken();
      // When impersonation ends (had token, now don't), clear cache and remove stale header
      if (previousAdminToken && !currentAdminToken) {
        clearCreditQueries();
        getEnv().apiFactory.clearAdminToken();
      }
      previousAdminToken = currentAdminToken;
    }, 1000);

    return () => clearInterval(interval);
  }, [getAdminToken, clearCreditQueries]);

  return { isAppLoading, forceInit: init };
};

const trackLoginEvent = (userId: string) => {
  const authMethod =
    (getAuthMethod() as AnalyticAuthType) || AnalyticAuthType.Google;
  // Don't clear here - Mixpanel identification still needs it for signup_method

  gtmService.sendEvent({
    event: AnalyticsEventTypes.LoginSuccess,
    params: {
      user_id: userId,
      method: authMethod,
    },
  });
};

const recordLead = (
  params: Pick<UserPayload, 'email' | 'firstName' | 'lastName'>,
) => {
  try {
    window.LeadDyno.recordLead(params.email, {
      first_name: params.firstName,
      last_name: params.lastName,
    });
  } catch (e) {
    console.error(e);
    return false;
  }
};
